import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import './AdminInvoiceView.css';

function AdminInvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ 
    status: '', 
    paid_date: '' 
  });

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['admin-invoice', id],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/invoices/${id}`);
      return res.data;
    },
  });

  // Gauti sąskaitos faktūros HTML peržiūrai
  const { data: invoiceHtml } = useQuery({
    queryKey: ['admin-invoice-html', id],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(
        `/api/admin/invoices/${id}/pdf?format=html`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(sessionId && { 'x-session-id': sessionId }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Nepavyko gauti sąskaitos faktūros HTML');
      }

      return await response.text();
    },
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.put(`/admin/invoices/${id}/status`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-invoice', id]);
      queryClient.invalidateQueries(['admin-invoices']);
      setShowStatusModal(false);
      setStatusForm({ status: '', paid_date: '' });
      notification.success('Sąskaitos faktūros statusas atnaujintas');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || 'Klaida atnaujinant statusą');
    },
  });

  const handleStatusUpdate = () => {
    if (!statusForm.status) {
      notification.warning('Pasirinkite statusą');
      return;
    }
    updateStatusMutation.mutate(statusForm);
  };

  const handlePrint = async (format = 'pdf') => {
    try {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(
        `/api/admin/invoices/${id}/pdf?format=${format}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(sessionId && { 'x-session-id': sessionId }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('PDF generavimas nepavyko');
      }

      if (format === 'pdf') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `saskaita-${invoice?.invoice_number || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // HTML formatas - atidaryti naujame lange
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
        }
      }
    } catch (error) {
      notification.error('Nepavyko atsisiųsti dokumento. Patikrinkite serverio logus.');
    }
  };

  // Removed unused functions: getStatusBadgeClass, getStatusLabel, formatDate, formatAddress

  if (isLoading) {
    return <div className="admin-invoice-view-page container">Kraunama...</div>;
  }

  if (!invoice) {
    return (
      <div className="admin-invoice-view-page container">
        <p>Sąskaita faktūra nerasta</p>
        <Link to="/admin/invoices" className="btn btn-primary">
          Grįžti į sąrašą
        </Link>
      </div>
    );
  }

  return (
    <div className="admin-invoice-view-page container">
      <div className="invoice-header">
        <div>
          <button
            onClick={() => navigate('/admin/invoices')}
            className="btn btn-secondary"
          >
            ← Grįžti
          </button>
        </div>
        <div className="invoice-actions">
          <button
            onClick={() => handlePrint('html')}
            className="btn btn-secondary"
          >
            👁️ Peržiūrėti HTML
          </button>
          <button
            onClick={() => handlePrint('pdf')}
            className="btn btn-primary"
          >
            📄 Atsisiųsti PDF
          </button>
          <button
            onClick={() => setShowStatusModal(true)}
            className="btn btn-secondary"
          >
            Keisti statusą
          </button>
        </div>
      </div>

      {/* Sąskaitos faktūros peržiūra */}
      <div className="invoice-preview-wrapper">
        {invoiceHtml ? (
          <iframe
            srcDoc={invoiceHtml}
            className="invoice-preview-iframe"
            title="Sąskaitos faktūros peržiūra"
          />
        ) : (
          <div className="loading">Kraunama sąskaita faktūra...</div>
        )}
      </div>

      {/* Statuso keitimo modalas */}
      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Keisti sąskaitos faktūros statusą</h2>
            
            <div className="form-group">
              <label>Statusas *</label>
              <select
                value={statusForm.status}
                onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                className="input"
                required
              >
                <option value="">Pasirinkite statusą</option>
                <option value="draft">Juodraštis</option>
                <option value="sent">Išsiųsta</option>
                <option value="paid">Apmokėta</option>
                <option value="cancelled">Atšaukta</option>
              </select>
            </div>

            {statusForm.status === 'paid' && (
              <div className="form-group">
                <label>Apmokėjimo data</label>
                <input
                  type="date"
                  value={statusForm.paid_date}
                  onChange={(e) => setStatusForm({ ...statusForm, paid_date: e.target.value })}
                  className="input"
                />
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
              >
                Atšaukti
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={updateStatusMutation.isLoading || !statusForm.status}
                className="btn btn-primary"
              >
                {updateStatusMutation.isLoading ? 'Atnaujinama...' : 'Atnaujinti'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInvoiceView;
